import { useState, useEffect, useMemo } from 'react';
import { Check } from 'lucide-react';
import { XIcon, TrashIcon } from '@phosphor-icons/react';
import cx from 'classnames';
import { formatFullName } from '../../utils/functions';
import { Avatar, Tabs } from '@chakra-ui/react';
import { ITeam } from '../../models/Team';
import { useEndpoints } from '../../hooks/useEndpoints';
import { queryClient } from '../../constants/queryClient';
import { USER_TYPES } from '../../enums/user';
import { genericLoadOptions } from '../../lib/DurationPicker/utils';
import { LEVELS_SPORT_OPTIONS, TYPE_TEAM_OPTIONS } from '../../constants/config';
import GenericModalV2 from '../../shared/GenericModal/GenericModal';
import CustomButton from '../../shared/CustomButton/CustomButton';
import CustomTabsV3 from '../../shared/CustomTabsV3/CustomTabsV3';
import Typhography from '../../shared/Typhography/Typhography';
import CustomSelectInput from '../../shared/CustomSelectInput/CustomSelectInput';
import { useConfirmModal } from '../../hooks/useConfirmModal/useConfirmModal';
import toast from 'react-hot-toast';
import { useModuleVisibility } from '../../hooks/useModuleVisibility';
import styles from './TeamSettingsModal.module.scss';

interface TeamSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: ITeam<any>;
  onDeleteTeam: () => void;
}

const ALL_TEAM_SETTINGS_TABS = [
  { label: 'Informazioni', value: 'info', visibilityKey: 'team-settings-info' },
  { label: 'Coaches', value: 'coaches', visibilityKey: 'team-settings-coaches' },
  { label: 'Atleti', value: 'athletes', visibilityKey: 'team-settings-athletes' },
  { label: 'Impostazioni', value: 'settings', visibilityKey: 'team-settings-settings' },
];

export default function TeamSettingsModal({ isOpen, onClose, team, onDeleteTeam }: TeamSettingsModalProps) {
  const { isTabVisible } = useModuleVisibility();

  // Tab visibili secondo i feature-flag runtime
  const tabs = useMemo(
    () => ALL_TEAM_SETTINGS_TABS.filter((t) => isTabVisible(t.visibilityKey)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isTabVisible]
  );

  const [activeTab, setActiveTab] = useState('info');
  const [localTeamName, setLocalTeamName] = useState(team.name);
  const [coachesDraft, setCoachesDraft] = useState<any[]>([]);
  const [athletesDraft, setAthletesDraft] = useState<any[]>([]);
  const [localIsPreferred, setLocalIsPreferred] = useState(team.isPreferred ?? false);
  const [isSaving, setIsSaving] = useState(false);

  const { getUsers, patchTeam } = useEndpoints();
  const { handleOpen: openConfirmModal } = useConfirmModal();

  useEffect(() => {
    if (isOpen) {
      setLocalTeamName(team.name);
      setCoachesDraft((team as any).coaches ?? []);
      setAthletesDraft((team as any).users ?? []);
      setLocalIsPreferred(team.isPreferred ?? false);
      // Apri sul primo tab visibile (default 'info' se tutti i flag sono assenti)
      setActiveTab(tabs[0]?.value ?? 'info');
    }
  }, [isOpen, team, tabs]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await patchTeam(team._id, {
        name: localTeamName,
        coaches: coachesDraft.map((c: any) => c._id),
        users: athletesDraft.map((a: any) => a._id),
        isPreferred: localIsPreferred,
      } as any);
      queryClient.invalidateQueries({ queryKey: ['user', team._id] });
      toast.success('Impostazioni salvate');
      onClose();
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  const spaceType: string = (team.spaceId as any)?.type ?? '';
  const sportLabel = TYPE_TEAM_OPTIONS.find(o => o.value === spaceType)?.label ?? spaceType;
  const levelOptions = (LEVELS_SPORT_OPTIONS as Record<string, { value: string; label: string }[]>)[spaceType] ?? [];
  const levelLabel = levelOptions.find(o => o.value === team.level)?.label ?? team.level;

  const coachLoadOptions = genericLoadOptions(
    getUsers,
    (user: any) => ({ label: formatFullName(user.firstName, user.lastName), value: user }),
    { type: USER_TYPES.COACH }
  );

  const athleteLoadOptions = genericLoadOptions(
    getUsers,
    (user: any) => ({ label: formatFullName(user.firstName, user.lastName), value: user }),
    { type: USER_TYPES.ATHLETE }
  );

  const renderPersonList = (
    items: any[],
    onRemove: (id: string) => void,
    emptyText: string
  ) => {
    if (items.length === 0) {
      return (
        <div className={styles.emptyList}>
          <Typhography size="regular-14" className="color-text-3">{emptyText}</Typhography>
        </div>
      );
    }
    return (
      <div className={styles.personList}>
        {items.map((person: any) => {
          const fullName = formatFullName(person.firstName, person.lastName);
          const imgSrc = person.image?.image
            ? `${process.env.REACT_APP_BASE_URL_IMAGE}${person.image.image}`
            : undefined;
          return (
            <div key={person._id} className={styles.personItem}>
              <div className={styles.personInfo}>
                <Avatar.Root className={styles.personAvatar}>
                  <Avatar.Fallback name={fullName} />
                  <Avatar.Image src={imgSrc} />
                </Avatar.Root>
                <div className={styles.personMeta}>
                  <Typhography size="bold-14">{fullName}</Typhography>
                  <Typhography size="medium-12" className="color-text-2">{person.email}</Typhography>
                </div>
              </div>
              <button className={styles.removeBtn} onClick={() => onRemove(person._id)}>
                <XIcon size={16} />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={onClose}
      title="Impostazioni team"
      size="xl"
      isLoading={isSaving}
      Footer={
        <>
          <CustomButton color="outline" onClick={onClose} disabled={isSaving}>
            Annulla
          </CustomButton>
          <CustomButton color="secondary" onClick={handleSave} isLoading={isSaving}>
            Salva modifiche
          </CustomButton>
        </>
      }
    >
      <CustomTabsV3
        value={activeTab}
        tabs={tabs}
        onValueChange={(e) => setActiveTab(e.value)}
      >
        {/* ─── Tab Informazioni ─────────────────────────────────────────────── */}
        <Tabs.Content value="info">
          <div className={styles.tabContent}>
            <div className={styles.sectionBlock}>
              <div className={styles.sectionLabel}>
                <span className={styles.sectionTitle}>Nome team</span>
              </div>
              <input
                type="text"
                value={localTeamName}
                onChange={(e) => setLocalTeamName(e.target.value)}
                className={styles.formInput}
                placeholder="Nome del team"
              />
            </div>

            <div className={styles.twoColRow}>
              <div className={styles.sectionBlock}>
                <div className={styles.sectionLabel}>
                  <span className={styles.sectionTitle}>Sport</span>
                </div>
                <div className={styles.readonlyField}>
                  {sportLabel || '—'}
                </div>
              </div>
              <div className={styles.sectionBlock}>
                <div className={styles.sectionLabel}>
                  <span className={styles.sectionTitle}>Livello</span>
                </div>
                <div className={styles.readonlyField}>
                  {levelLabel || '—'}
                </div>
              </div>
            </div>
          </div>
        </Tabs.Content>

        {/* ─── Tab Coaches ──────────────────────────────────────────────────── */}
        <Tabs.Content value="coaches">
          <div className={styles.tabContent}>
            <div className={styles.sectionBlock}>
              <div className={styles.sectionLabel}>
                <span className={styles.sectionTitle}>Aggiungi coach</span>
                <span className={styles.sectionDesc}>Cerca e aggiungi coach al team</span>
              </div>
              <CustomSelectInput
                placeholder="Cerca per nome o email..."
                value={null}
                loadOptions={coachLoadOptions}
                defaultOptions
                onChange={(option: any) => {
                  const coach = option?.value ?? option;
                  if (!coach?._id) return;
                  if (coachesDraft.some((c: any) => c._id === coach._id)) return;
                  setCoachesDraft(prev => [...prev, coach]);
                }}
                originValue
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className={styles.sectionBlockBordered}>
              <div className={styles.sectionLabel}>
                <span className={styles.sectionTitle}>
                  Coaches del team <span className={styles.countBadge}>{coachesDraft.length}</span>
                </span>
              </div>
              {renderPersonList(
                coachesDraft,
                (id) => setCoachesDraft(prev => prev.filter((c: any) => c._id !== id)),
                'Nessun coach assegnato'
              )}
            </div>
          </div>
        </Tabs.Content>

        {/* ─── Tab Atleti ───────────────────────────────────────────────────── */}
        <Tabs.Content value="athletes">
          <div className={styles.tabContent}>
            <div className={styles.sectionBlock}>
              <div className={styles.sectionLabel}>
                <span className={styles.sectionTitle}>Aggiungi atleta</span>
                <span className={styles.sectionDesc}>Cerca e aggiungi atleti al team</span>
              </div>
              <CustomSelectInput
                placeholder="Cerca per nome o email..."
                value={null}
                loadOptions={athleteLoadOptions}
                defaultOptions
                onChange={(option: any) => {
                  const athlete = option?.value ?? option;
                  if (!athlete?._id) return;
                  if (athletesDraft.some((a: any) => a._id === athlete._id)) return;
                  setAthletesDraft(prev => [...prev, athlete]);
                }}
                originValue
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className={styles.sectionBlockBordered}>
              <div className={styles.sectionLabel}>
                <span className={styles.sectionTitle}>
                  Atleti del team <span className={styles.countBadge}>{athletesDraft.length}</span>
                </span>
              </div>
              {renderPersonList(
                athletesDraft,
                (id) => setAthletesDraft(prev => prev.filter((a: any) => a._id !== id)),
                'Nessun atleta assegnato'
              )}
            </div>
          </div>
        </Tabs.Content>

        {/* ─── Tab Impostazioni ─────────────────────────────────────────────── */}
        <Tabs.Content value="settings">
          <div className={styles.tabContent}>
            <div className={styles.sectionBlock}>
              <div className={styles.sectionLabel}>
                <span className={styles.sectionTitle}>Preferenze</span>
                <span className={styles.sectionDesc}>Configura le preferenze di visualizzazione del team</span>
              </div>
              <label className={cx(styles.testItem, localIsPreferred && styles.testItemActive)} style={{ padding: '12px 16px' }}>
                <input
                  type="checkbox"
                  checked={localIsPreferred}
                  onChange={() => setLocalIsPreferred(prev => !prev)}
                  className={styles.hiddenInput}
                />
                <div className={cx(styles.checkboxSquare, localIsPreferred && styles.checkboxSquareChecked)}>
                  {localIsPreferred && <Check className={styles.checkIcon} strokeWidth={3} />}
                </div>
                <div>
                  <Typhography size="medium-14">Team in evidenza</Typhography>
                  <Typhography size="regular-12" className="color-text-2">Mostra questo team in cima alla lista</Typhography>
                </div>
              </label>
            </div>

            <div className={styles.dangerZone}>
              <div className={styles.sectionLabel}>
                <span className={styles.sectionTitle} style={{ color: '#e53e3e' }}>Zona pericolosa</span>
                <span className={styles.sectionDesc}>Questa azione non può essere annullata</span>
              </div>
              <button
                className={styles.deleteTeamBtn}
                onClick={() => {
                  onClose();
                  openConfirmModal({
                    title: 'Elimina team',
                    description: `Sei sicuro di voler eliminare il team "${team.name}"? Il team verrà rimosso e gli eventi futuri non ancora compilati verranno aggiornati.`,
                    onConfirm: onDeleteTeam,
                  });
                }}
              >
                <TrashIcon size={16} />
                <span>Elimina team</span>
              </button>
            </div>
          </div>
        </Tabs.Content>
      </CustomTabsV3>
    </GenericModalV2>
  );
}
